import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageDetect } from '@/hooks/useLanguageDetect';
import { supabase } from '@/lib/supabase';

interface Category {
  id: string;
  name: string;
  name_zh_tw: string;
  slug: string;
  icon: string;
  description: string;
}

interface Article {
  id: string;
  title: string;
  title_zh_tw: string;
  slug: string;
  summary: string;
  summary_zh_tw: string;
  image_url: string;
  tags: string[];
  created_at: string;
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function CategoryPage() {
  const { t } = useTranslation();
  const { language } = useLanguageDetect();
  const { slug } = useParams<{ slug: string }>();

  const [category, setCategory] = useState<Category | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchCategoryData() {
      if (!slug) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch category info
        const { data: catData, error: catError } = await supabase
          .from('categories')
          .select('id, name, name_zh_tw, slug, icon, description')
          .eq('slug', slug)
          .maybeSingle();

        if (catError) throw catError;
        if (!catData) {
          setError('分类不存在');
          setLoading(false);
          return;
        }

        setCategory(catData);

        // Fetch articles in this category
        const { data: articleData, error: articleError } = await supabase
          .from('articles')
          .select('id, title, title_zh_tw, slug, summary, summary_zh_tw, image_url, tags, created_at')
          .eq('category_id', catData.id)
          .eq('is_published', true)
          .order('created_at', { ascending: false });

        if (articleError) throw articleError;
        setArticles(articleData || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载失败');
      } finally {
        setLoading(false);
      }
    }

    fetchCategoryData();
  }, [slug]);

  if (loading) {
    return (
      <main className="w-full min-h-[60vh]">
        {/* Hero skeleton */}
        <section className="w-full bg-primary-600 py-14 md:py-20 px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/20 mx-auto mb-5 animate-pulse" />
            <div className="w-48 h-8 bg-white/20 rounded mx-auto mb-3 animate-pulse" />
            <div className="w-80 h-4 bg-white/20 rounded mx-auto animate-pulse" />
          </div>
        </section>

        {/* Articles skeleton */}
        <section className="w-full py-10 md:py-14 bg-background-50 px-4 md:px-8">
          <div className="max-w-4xl mx-auto space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-4 md:gap-6 bg-background-50 rounded-xl border border-background-200 p-4 md:p-5">
                <div className="w-full sm:w-48 h-36 rounded-lg bg-background-200 animate-pulse flex-shrink-0" />
                <div className="flex-1">
                  <div className="w-full h-5 bg-background-200 rounded animate-pulse mb-2" />
                  <div className="w-3/4 h-4 bg-background-200 rounded animate-pulse mb-2" />
                  <div className="w-1/2 h-4 bg-background-200 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    );
  }

  if (error || !category) {
    return (
      <main className="w-full min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-foreground-400" />
          </div>
          <h2 className="font-heading text-lg font-bold text-foreground-900 mb-2">
            {error || '分类未找到'}
          </h2>
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            <i className="ri-arrow-left-line text-xs" />
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const displayName = language === 'zh-Hans' ? category.name : category.name_zh_tw;

  return (
    <main className="w-full min-h-[60vh]">
      {/* Category Hero */}
      <section className="w-full bg-primary-600 py-14 md:py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/15 text-white mb-5">
            <i className={`${category.icon} text-3xl`} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
            {displayName}
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
            {category.description}
          </p>
          <div className="mt-5 flex items-center justify-center gap-2 text-white/60 text-xs">
            <i className="ri-article-line" />
            <span>共 {articles.length} 篇文章</span>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="w-full bg-background-100 border-b border-background-200/70 px-4 md:px-8 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-900 font-medium">{displayName}</span>
        </div>
      </section>

      {/* Articles List */}
      <section className="w-full py-10 md:py-14 bg-background-50 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {articles.length > 0 ? (
            <div className="space-y-6">
              {articles.map((article) => {
                const displayTitle = language === 'zh-Hans' ? article.title : article.title_zh_tw;
                const displaySummary = language === 'zh-Hans' ? article.summary : article.summary_zh_tw;

                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col sm:flex-row gap-4 md:gap-6 bg-background-50 rounded-xl border border-background-200 hover:border-primary-300 transition-colors p-4 md:p-5"
                  >
                    <div className="w-full sm:w-48 h-36 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        alt={displayTitle}
                        title={displayTitle}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        src={article.image_url}
                      />
                    </div>
                    <div className="flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                          {displayName}
                        </span>
                        <span className="text-xs text-foreground-400">{formatDate(article.created_at)}</span>
                      </div>
                      <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors">
                        {displayTitle}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
                        {displaySummary}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-primary-600 font-medium mt-3 group-hover:text-primary-700 transition-colors">
                        {t('readMore')}
                        <i className="ri-arrow-right-line" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                <i className="ri-article-line text-2xl text-foreground-400" />
              </div>
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                暂无文章
              </h2>
              <p className="text-sm text-foreground-500 mb-4">
                该分类下暂时没有已发布的文章
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-1 bg-primary-500 hover:bg-primary-600 text-white px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
              >
                <i className="ri-arrow-left-line text-xs" />
                返回首页
              </Link>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}